import {BaseSectionStatus} from './base-section-status'
import type {MergeBoxSectionStatus} from './types'
import type {
  JSONAPIPullRequestPayload,
  PullRequestMergeRequirementsPayload,
  StackMergeConditionPayload,
} from '../../page-data/payloads/merge-box'
import type {StatusChecksPageData} from '../../page-data/payloads/status-checks'
import type {StackMergeabilityPageData} from '../../page-data/payloads/stack-mergeability'
import {getStackCondition} from '../json-api-helpers'
import type {UseQueryResult} from '@github-ui/react-query'
import {FeatureState, type FeatureStates} from '@github-ui/pull-requests/page-data/payloads/header'
import type {AutoMergeableStackEntry, StackSectionStackEntry, StackSectionStatusType} from './stack-section-types'
import {buildCurrentStackEntries} from './stack-entries/build-current-stack-entries'
import {EffectiveEntryStatus} from '../effective-entry-status'
import type {PullRequestMergeAction} from '../../types'
import type {PullRequestUserActionStack} from '@github-ui/pull-requests/analytics/pull-request-user-action'

export type {
  AliveChannels,
  AutoMergeableStackEntry,
  StackSectionEntryStatus,
  StackSectionEntryType,
  StackSectionStackEntry,
  StackSectionStatusType,
} from './stack-section-types'

function isAutoMergeableEntry(entry: StackSectionStackEntry): entry is AutoMergeableStackEntry {
  return (
    (entry.position === 'BEFORE' || entry.position === 'CURRENT') &&
    entry.effectiveEntryStatus !== EffectiveEntryStatus.Merged &&
    entry.effectiveEntryStatus !== EffectiveEntryStatus.Queued
  )
}

export class StackSectionStatus extends BaseSectionStatus<StackSectionStatusType> {
  #stackCondition: StackMergeConditionPayload | undefined
  #stackEntries: StackSectionStackEntry[] | undefined
  #loadingEntryCount: number | undefined
  #isRebasing: boolean
  #pendingStackMergeAction: PullRequestMergeAction | undefined

  constructor(
    pullRequest: JSONAPIPullRequestPayload,
    mergeRequirements: PullRequestMergeRequirementsPayload | null,
    statusChecks: StatusChecksPageData | undefined,
    featureStates: FeatureStates,
    stackMergeabilityQueries: Array<UseQueryResult<StackMergeabilityPageData>> | undefined = undefined,
    isRebasing = false,
    pendingStackMergeAction?: PullRequestMergeAction,
  ) {
    super(pullRequest, mergeRequirements, statusChecks, featureStates, stackMergeabilityQueries)
    this.#isRebasing = isRebasing
    this.#pendingStackMergeAction = pendingStackMergeAction
  }

  override get shouldRender() {
    return this.featureStates.pullRequestStacks === FeatureState.Enabled && this.isStacked
  }

  // When the closed/merged or merge-queue sections take over the MergeBox,
  // the StackSection should collapse to just its nav list of stacked pull
  // requests — the surrounding section header and rebase actions don't apply.
  get shouldOnlyRenderStackList(): boolean {
    return this.pullRequest.state !== 'OPEN' || this.pullRequest.isInMergeQueue
  }

  override get sectionStatus(): StackSectionStatusType {
    const currentEntry = this.stackEntries?.find(e => e.position === 'CURRENT')
    let anyLoading = false

    switch (this.stackCondition?.result || 'UNKNOWN') {
      case 'FAILED':
        if (currentEntry?.failedSubConditions && currentEntry.failedSubConditions.length > 0) {
          // This stack entry has failed to meet some condition
          return 'FAILED'
        } else {
          // A previous entry in the stack has failed to meet some condition
          return 'BLOCKED'
        }
      case 'UNKNOWN':
        return 'UNKNOWN'
      case 'PASSED':
        for (const entry of this.stackEntries ?? []) {
          // We don't consider upstack entries for section status calculations
          if (entry.position === 'AFTER') continue

          switch (entry.effectiveEntryStatus) {
            case EffectiveEntryStatus.NotReady:
            case EffectiveEntryStatus.BlockedDownstack:
            case EffectiveEntryStatus.Draft:
            case EffectiveEntryStatus.Closed:
            case EffectiveEntryStatus.MergeConflicts:
            case EffectiveEntryStatus.NeedsRebase:
              return 'BLOCKED'
            case EffectiveEntryStatus.Loading:
              anyLoading = true
              break
          }
        }

        return anyLoading ? 'LOADING_MERGE_STATE' : 'PASSED'
    }
  }

  override get mergeBoxStatus(): MergeBoxSectionStatus {
    const sectionStatus = this.sectionStatus

    switch (sectionStatus) {
      case 'BLOCKED':
        return 'FAILED'
      default:
        return sectionStatus
    }
  }

  get isStacked() {
    return !!this.stackCondition?.isStacked
  }

  get stackEntries(): StackSectionStackEntry[] | undefined {
    if (this.#stackEntries) {
      return this.#stackEntries
    }

    const entries = this.stackCondition?.entries
    if (!entries) {
      return undefined
    }

    const queries = this.stackMergeabilityQueries ?? []
    this.#stackEntries = buildCurrentStackEntries(entries, {
      mergeRequirements: this.mergeRequirements,
      pendingStackMergeAction: this.#pendingStackMergeAction,
      isRebasing: this.#isRebasing,
      queries,
      statusChecks: this.statusChecks,
    })
    return this.#stackEntries
  }

  get loadingEntryCount() {
    if (this.#loadingEntryCount === undefined) {
      this.#loadingEntryCount = (this.stackEntries ?? []).filter(
        entry => entry.effectiveEntryStatus === EffectiveEntryStatus.Loading,
      ).length
    }
    return this.#loadingEntryCount
  }

  // True when the stack cannot be merged right now because the CURRENT entry
  // or a BEFORE entry is in draft, has pending checks, or is otherwise in a
  // blocked state. Used by the merge button to disable "Enqueue pull request" /
  // "Enqueue stack (#)" actions in cases where waiting on auto-merge would never
  // succeed.
  get isMergeCurrentlyBlocked(): boolean {
    if (!this.isStacked) return false

    return (this.stackEntries ?? []).some(e => {
      if (e.position === 'AFTER') return false

      if (
        e.effectiveEntryStatus === EffectiveEntryStatus.NotReady ||
        e.effectiveEntryStatus === EffectiveEntryStatus.Draft ||
        e.effectiveEntryStatus === EffectiveEntryStatus.Closed ||
        e.effectiveEntryStatus === EffectiveEntryStatus.BlockedDownstack ||
        e.effectiveEntryStatus === EffectiveEntryStatus.MergeConflicts ||
        e.effectiveEntryStatus === EffectiveEntryStatus.NeedsRebase
      ) {
        return true
      }

      return false
    })
  }

  get stackCondition() {
    if (!this.#stackCondition) {
      this.#stackCondition = getStackCondition(this.mergeRequirements)
    }
    return this.#stackCondition
  }

  // Count of entries in the stack that will be merged if the user merges the current pull request.
  // This includes the current entry and any entries before it in the stack, excluding entries that
  // are already merged or currently queued in the merge queue.
  get entriesToMergeCount(): number {
    return (
      this.stackEntries?.filter(e => {
        if (e.position === 'AFTER') return false
        if (e.effectiveEntryStatus === EffectiveEntryStatus.Merged) return false
        if (e.effectiveEntryStatus === EffectiveEntryStatus.Queued) return false

        return true
      }).length ?? 0
    )
  }

  // CURRENT and BEFORE entries that are not already merged or queued in the merge queue.
  get entriesThatCanBeAutoMerged(): AutoMergeableStackEntry[] {
    return this.stackEntries?.filter(isAutoMergeableEntry) ?? []
  }

  // Count of entries in the stack that are Before.
  // This includes the current entry and any entries before it in the stack, excluding entries that
  // are already merged or currently queued in the merge queue.
  get eligibleToMergeEntriesBeforeCount(): number {
    return (
      this.stackEntries?.filter(e => {
        return (
          e.position === 'BEFORE' &&
          e.effectiveEntryStatus !== EffectiveEntryStatus.Merged &&
          e.effectiveEntryStatus !== EffectiveEntryStatus.Queued
        )
      }).length ?? 0
    )
  }

  get stackMetadata(): PullRequestUserActionStack | undefined {
    if (this.stackCondition?.stack) {
      return {
        id: this.stackCondition.stack.id,
        number: this.stackCondition.stack.number,
        created_at: this.stackCondition.stack.createdAt,
      }
    }

    return undefined
  }

  get isAsyncStackMergePending(): boolean {
    return this.#pendingStackMergeAction !== undefined
  }

  get isStackMergeInProgress(): boolean {
    return (
      this.isStacked &&
      (this.isAsyncStackMergePending || !!this.pullRequest.autoMergeRequest || this.pullRequest.isInMergeQueue)
    )
  }
}
